import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, EmptyState } from '@weezevent/nacre';
import { ResponsiveLine } from '@nivo/line';
import Styles from './graphHomeEvent.module.css';

function GraphTooltipContent({ point }) {
    const { t } = useTranslation();

    return (
        <div className={Styles['tooltip-container']}>
            <div className={Styles['tooltip-square']} style={{ backgroundColor: point.serieColor }}></div>
            <div>
                {t('ticket.eventDashboard.tickets')}: <span className={Styles['tooltip-value']}>{`${point.data.yFormatted},`}&nbsp;</span>
            </div>
            <div>
                {t('ticket.eventDashboard.date')}: <span className={Styles['tooltip-value']}>{point.data.xFormatted}</span>
            </div>
        </div>
    );
}

export default function GraphHomeEvent({ stats }) {
    const { t } = useTranslation();

    const statsNotEmpty = React.useMemo(() => {
        const totalValue = stats.reduce((acc, current) => {
            const value = current.data?.reduce((a, b) => {
                return a + b.y;
            }, 0);
            return acc + value;
        }, 0);
        return totalValue > 0;
    }, [stats]);

    if (!stats) {
        return null;
    }

    const maxValue = React.useMemo(() => Math.max(Math.max(...stats[0].data.map(s => s.y)), Math.max(...stats[1].data.map(s => s.y))), [stats]);
    const scaleGraph = React.useMemo(() => {
        const sizes = [
            { x: 999999, size: 'huge' },
            { x: 99999, size: 'wider' },
            { x: 9999, size: 'wide' }
        ];
        return sizes.find(s => maxValue > s.x)?.size || 'default';
    }, [maxValue]);

    return statsNotEmpty ? (
        <div className={Styles['responsive-line-container']} scale={scaleGraph}>
            <ResponsiveLine
                data={stats}
                colors={stats.map(item => item.color)}
                margin={{ top: 20, right: 20, bottom: 100, left: 40 }}
                gridXValues={6}
                yScale={{
                    type: 'linear',
                    stacked: false
                }}
                theme={{
                    background: '#ffffff',
                    legends: {
                        text: {
                            fill: '#000000'
                        }
                    }
                }}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    legendOffset: 36,
                    legendPosition: 'middle',
                    tickRotation: -80
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legendOffset: -40,
                    tickValues: 9
                }}
                pointSize={15}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                useMesh={true}
                tooltip={({ point }) => <GraphTooltipContent point={point} />}
                legends={[
                    {
                        data: stats.map(item => {
                            return {
                                color: item.color,
                                id: item.id,
                                label: item.label
                            };
                        }),
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: 100,
                        itemsSpacing: 0,
                        itemDirection: 'left-to-right',
                        itemWidth: 100,
                        itemHeight: 20,
                        itemOpacity: 0.75,
                        symbolSize: 12,
                        symbolShape: 'circle',
                        symbolBorderColor: 'rgba(0, 0, 0, .5)',
                        effects: [
                            {
                                on: 'hover',
                                style: {
                                    itemOpacity: 1
                                }
                            }
                        ]
                    }
                ]}
            />
        </div>
    ) : (
        <EmptyState icon={<Icon name="list" />} title={t('ticket.eventDashboard.emptyChartState.title')} subtitle={t('ticket.eventDashboard.emptyChartState.info')} />
    );
}
